import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Container,
  Grid,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useLocation, useNavigate } from "react-router-dom";

import {
  getApplicant,
  getApplications,
  registerApplicant,
  sendApplicantOtp,
  verifyApplicantOtp,
  resolvePhoneApplicant,
  checkApplicantContact,
  applicantErrorMessage,
  applicantDocPublicUrl,
  extractExperienceCertificate,
  extractEducationCertificate,
} from "../../services/backend/ApplicantService";
import { MuiTelInput } from "mui-tel-input";

import {
  sendPhoneCode,
  confirmPhoneCode,
  phoneAuthErrorMessage,
  toE164,
  isValidPhone,
  clearRecaptcha,
} from "../../services/auth/PhoneVerification";
import { NO_SMS_HINT, SMS_TEST_MODE_VISIBLE, phoneSendInfo, useResendCooldown } from "./phoneGate";
import {
  getApplicantConfig,
  DEFAULT_APPLICANT_CONFIG,
  docsForStage,
  requiredDocsForStage,
  activeApplications,
  requiredStage1For,
  callName,
  institutionOptions,
  openInstitutionOptions,
  openLevelsOf,
  academicLevelsOf,
  profileDocs,
  profileDocForSection,
} from "../../services/backend/ApplicantConfigService";
import {
  docFiles,
  docStatusRollup,
  entryNeedsEvidence,
  filesForEntry,
} from "../../components/applicants/applicantLogic";
import {
  APPLICANT_FACING_STATUS_LABELS,
  DOC_FILE_STATUS_LABELS,
  labelForStatus,
} from "../../components/applicants/applicantLabels";
import {
  buildApplicantData,
  applicantToForm,
  hasEducationEntry,
  hasExperienceEntry,
} from "./applicantPayload";
import DocumentSlots from "./DocumentSlots";
import { EducationList, ExperienceList } from "./ProfileEntryLists";
import ApplicantPhotoUpload from "./ApplicantPhotoUpload";
import { useApplicantSession } from "./applicantSession";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const norm = (v) => String(v || "").trim().toLowerCase();

// Hidden container id hosting the invisible reCAPTCHA for Firebase Phone Auth.
const RECAPTCHA_ID = "recaptcha-applicant-continuar";

const GENERAL_POSTULACION = "general";

// Client-side decode of the magic-link token payload (base64url JSON). This only
// reads the nuip for UX resume; it is NOT a security boundary (data access is
// gated by Firestore rules).
function decodeToken(t) {
  try {
    const payloadB64 = String(t).split(".")[0];
    const pad = payloadB64.length % 4 === 0 ? "" : "=".repeat(4 - (payloadB64.length % 4));
    const json = atob(payloadB64.replace(/-/g, "+").replace(/_/g, "/") + pad);
    return JSON.parse(json);
  } catch {
    return null;
  }
}

export default function ApplicantContinue() {
  const location = useLocation();
  const navigate = useNavigate();
  const applicantSession = useApplicantSession();
  const tokenParam = useMemo(() => new URLSearchParams(window.location.search).get("t"), []);
  // Set right after registration so the profile opens without an OTP challenge.
  const justRegistered = !!location.state?.justRegistered;

  // Auth: "otp" (need login) or "resolved" (have nuip).
  const [nuip, setNuip] = useState(null);
  const [channel, setChannel] = useState("EMAIL"); // EMAIL | PHONE
  const [dest, setDest] = useState(""); // email or phone, per channel
  const [otpSent, setOtpSent] = useState(false); // email OTP sent
  const [phoneConfirmation, setPhoneConfirmation] = useState(null); // phone code sent
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);
  const loginCooldown = useResendCooldown();

  // Tear down the invisible reCAPTCHA when leaving the form.
  useEffect(() => () => clearRecaptcha(), []);

  // Reset the login state when switching channel.
  const onChannelChange = (_e, next) => {
    if (!next || next === channel) return;
    setChannel(next);
    setDest(""); setCode(""); setOtpSent(false); setPhoneConfirmation(null);
    setError(null); setInfo(null);
    clearRecaptcha();
  };

  const [applicant, setApplicant] = useState(null);
  const [applications, setApplications] = useState([]); // aspirant's applications
  const [selectedPostId, setSelectedPostId] = useState(GENERAL_POSTULACION);
  const [applying, setApplying] = useState(null); // callId being applied to
  const [files, setFiles] = useState({}); // { [type]: (File|undefined)[] } — docs de la convocatoria
  // Documentos de perfil pendientes de guardar: los sueltos (cédula…) y los
  // soportes por registro { [docKey]: { [entryId]: (File|undefined)[] } }.
  const [profileFiles, setProfileFiles] = useState({});
  const [evidence, setEvidence] = useState({});
  const [uploaded, setUploaded] = useState(false);
  // Applicants config (documents + labels + max file size) — live source of truth.
  const [cfg, setCfg] = useState(DEFAULT_APPLICANT_CONFIG);

  useEffect(() => { getApplicantConfig().then(setCfg); }, []);

  // --- Editable profile (self-service) ---
  const [form, setForm] = useState(null); // flat form, prefilled from the applicant
  // What the form looked like right after loading / saving: tells unsaved edits apart.
  const [formBaseline, setFormBaseline] = useState(null);
  const resetForm = (a) => {
    const next = applicantToForm(a);
    setForm(next);
    setFormBaseline(JSON.stringify(next));
  };
  const theme = useTheme();
  const wideMenu = useMediaQuery(theme.breakpoints.up("md"));
  // Profile section shown in the side menu. ?seccion= lets a link open one directly.
  const [section, setSection] = useState(
    () => new URLSearchParams(window.location.search).get("seccion") || "personal"
  );
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileError, setProfileError] = useState(null);
  const [profileSaved, setProfileSaved] = useState(false);
  // Profile photo — the cropped File is held here and uploaded with the profile
  // save; `photoPreview` is a local object URL shown until then.
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [removePhoto, setRemovePhoto] = useState(false);
  // Email re-verification — only required when the email actually changes.
  const [pfEmailOtpSent, setPfEmailOtpSent] = useState(false);
  const [pfEmailCode, setPfEmailCode] = useState("");
  const [pfEmailVerified, setPfEmailVerified] = useState(false);
  const [pfVerifiedEmail, setPfVerifiedEmail] = useState("");
  const [pfEmailInfo, setPfEmailInfo] = useState(null);
  // Phone re-verification — only required when the celular actually changes.
  const [pfPhoneConfirmation, setPfPhoneConfirmation] = useState(null);
  const [pfPhoneCode, setPfPhoneCode] = useState("");
  const [pfPhoneVerified, setPfPhoneVerified] = useState(false);
  const [pfVerifiedPhone, setPfVerifiedPhone] = useState("");
  const [pfPhoneToken, setPfPhoneToken] = useState(null);
  const [pfPhoneInfo, setPfPhoneInfo] = useState(null);
  const pfPhoneCooldown = useResendCooldown();

  // Post-registration path: nuip handed over via router state (no OTP needed).
  useEffect(() => {
    if (location.state?.nuip) setNuip(String(location.state.nuip));
  }, [location.state]);

  // Magic-link path: decode token → resolve nuip immediately.
  useEffect(() => {
    if (!tokenParam) return;
    const payload = decodeToken(tokenParam);
    if (payload?.nuip) setNuip(String(payload.nuip));
    else setError("El enlace no es válido o expiró. Ingresa con tu correo electrónico.");
  }, [tokenParam]);

  // Load the applicant + all their applications once we have a nuip.
  const loadApplications = async (id) => setApplications(await getApplications(id));
  useEffect(() => {
    if (!nuip) return;
    (async () => {
      setBusy(true);
      const [a, ps] = await Promise.all([getApplicant(nuip), getApplications(nuip)]);
      setApplicant(a);
      setApplications(ps);
      if (a) resetForm(a);
      setBusy(false);
      if (!a) setError("No encontramos tu postulación. Verifica el enlace o inicia sesión.");
    })();
  }, [nuip]);

  const handleSendOtp = async () => {
    if (!dest.trim()) { setError("Ingresa tu correo electrónico."); return; }
    setBusy(true); setError(null);
    const { ok, json } = await sendApplicantOtp({ recipient: dest.trim() });
    setBusy(false);
    if (!ok) { setError(json?.error || "No fue posible enviar el código."); return; }
    setOtpSent(true);
    setInfo(`Enviamos un código a ${dest.trim()}. Vence en ${json?.expiresInMinutes || 10} minutos.`);
  };

  const handleVerifyOtp = async () => {
    setBusy(true); setError(null);
    const { ok, json } = await verifyApplicantOtp({ recipient: dest.trim(), code: code.trim() });
    setBusy(false);
    if (!ok) { setError(json?.error === "OTP_EXPIRED" ? "El código expiró. Solicita uno nuevo." : "Código incorrecto."); return; }
    if (!json?.nuip) { setError("No hay una postulación asociada a ese contacto."); return; }
    setNuip(String(json.nuip));
  };

  // Phone channel: Firebase Phone Auth verifies the number, then the backend
  // resolves the applicant from the verified number (resolvePhoneApplicant).
  const handleSendPhone = async () => {
    if (!isValidPhone(dest)) { setError("Ingresa un número de celular válido."); return; }
    setBusy(true); setError(null);
    try {
      const confirmation = await sendPhoneCode(dest, RECAPTCHA_ID, { testMode: !!cfg.smsTestMode });
      setPhoneConfirmation(confirmation);
      setInfo(phoneSendInfo(toE164(dest), !!cfg.smsTestMode));
      loginCooldown.start();
    } catch (e) {
      setError(phoneAuthErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const handleVerifyPhone = async () => {
    if (!phoneConfirmation) return;
    setBusy(true); setError(null);
    try {
      const { idToken } = await confirmPhoneCode(phoneConfirmation, code);
      const { ok, json } = await resolvePhoneApplicant({ phoneToken: idToken });
      if (!ok || !json?.nuip) {
        setError(json?.error === "NO_APPLICATION"
          ? "No hay una postulación asociada a ese teléfono."
          : "No fue posible validar el teléfono. Intenta de nuevo.");
        return;
      }
      loginCooldown.reset();
      setNuip(String(json.nuip));
    } catch (e) {
      setError(phoneAuthErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  // The selected postulación drives the stage, catalog and document status.
  const application = applications.find((p) => p.callId === selectedPostId) || applications[0] || null;
  const activePostId = application?.callId || selectedPostId;
  const stage = application && ["SELECTED", "DOCS_STAGE2_PENDING"].includes(application.status) ? 2 : 1;
  // Stage 1 shows the call's own required subset; stage 2 the global set.
  const requiredKeys = stage === 2 ? requiredDocsForStage(cfg, 2) : requiredStage1For(cfg, activePostId);
  const stageDocs = docsForStage(cfg, stage).filter((d) => requiredKeys.includes(d.key));
  const maxFileBytes = cfg.maxFileBytes || DEFAULT_APPLICANT_CONFIG.maxFileBytes;
  const maxFileMb = Math.round(maxFileBytes / 1024 / 1024);
  const existingDocs = (application && application.documents) || {};
  const niveles = useMemo(() => academicLevelsOf(cfg), [cfg]);
  const instituciones = useMemo(() => institutionOptions(cfg), [cfg]);
  const institucionesAbiertas = useMemo(() => openInstitutionOptions(cfg), [cfg]);
  const nivelesAbiertos = useMemo(() => openLevelsOf(cfg), [cfg]);
  // Who the documents should belong to (names and cédula can't change here).
  const identityPerson = useMemo(() => (applicant
    ? { nuip: applicant.nuip, firstName: applicant.firstName, lastName: applicant.lastName }
    : null), [applicant]);

  // --- Documentos de perfil (de la persona, no de la convocatoria) -----------
  const storedProfileDocs = (applicant && applicant.profileDocuments) || {};
  const eduDoc = useMemo(() => profileDocForSection(cfg, "education"), [cfg]);
  const expDoc = useMemo(() => profileDocForSection(cfg, "workExperience"), [cfg]);
  const unboundProfileDocs = useMemo(() => profileDocs(cfg).filter((d) => !d.boundTo), [cfg]);
  // Archivos ya guardados de un registro concreto, y los recién escogidos.
  const storedFilesFor = (docKey) => (entryId) => filesForEntry(storedProfileDocs[docKey], entryId);
  const pendingEvidenceFor = (docKey) => (entryId) => (evidence[docKey]?.[entryId]) || [];
  const openStoredFile = (file) => {
    const url = applicantDocPublicUrl(cfg.storageBucket, file?.path);
    if (url) window.open(url, "_blank", "noopener");
  };

  // Cada selección guarda, además del archivo, a cuál de los ya cargados
  // reemplaza (`replaceFileId`); sin ese dato el intake sólo puede agregar.
  const handleEvidence = (docKey) => (entryId, slot, file, replaceFileId = null) => {
    if (file && file.size > maxFileBytes) { setProfileError(`"${file.name}" supera ${maxFileMb} MB.`); return; }
    setProfileError(null);
    setEvidence((prev) => {
      const byEntry = { ...(prev[docKey] || {}) };
      const arr = Array.isArray(byEntry[entryId]) ? [...byEntry[entryId]] : [];
      arr[slot] = file ? { file, replaceFileId } : undefined;
      byEntry[entryId] = arr;
      return { ...prev, [docKey]: byEntry };
    });
  };
  const handleProfileFile = (type, slot, file, replaceFileId = null) => {
    if (file && file.size > maxFileBytes) { setProfileError(`"${file.name}" supera ${maxFileMb} MB.`); return; }
    setProfileError(null);
    setProfileFiles((prev) => {
      const arr = Array.isArray(prev[type]) ? [...prev[type]] : [];
      arr[slot] = file ? { file, replaceFileId } : undefined;
      return { ...prev, [type]: arr };
    });
  };

  // Required-docs status for the current stage — drives the banner + chips.
  const faltantesObligatorios = requiredKeys.filter(
    (key) => docStatusRollup(existingDocs[key]) === "PENDING"
  );

  // Active calls the aspirant has NOT applied to yet.
  const otherCalls = activeApplications(cfg).filter(
    (p) => p.id !== GENERAL_POSTULACION && !applications.some((pp) => pp.callId === p.id)
  );

  // Profile edit gating: a changed email/phone must be re-verified before saving.
  const emailChanged = !!form && norm(form.email) !== norm(applicant && applicant.email);
  const phoneChanged = !!form && toE164(form.mobilePhone) !== ((applicant && applicant.mobilePhone) || "");
  const emailOk = !emailChanged || (pfEmailVerified && norm(pfVerifiedEmail) === norm(form && form.email));
  const phoneOk = !phoneChanged || (pfPhoneVerified && !!pfPhoneToken && pfVerifiedPhone === toE164(form && form.mobilePhone));
  const canSaveProfile = !!form && emailOk && phoneOk && !savingProfile;

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Photo shown right now: the pending crop, else the stored one (unless the
  // aspirant asked to remove it and hasn't saved yet).
  const storedPhotoUrl = applicant && !removePhoto
    ? applicantDocPublicUrl(cfg.storageBucket, applicant.photoPath)
    : null;
  const photoUrl = photoPreview || storedPhotoUrl;

  // Object URLs leak for the life of the document unless revoked.
  useEffect(() => {
    if (!photoPreview) return undefined;
    return () => URL.revokeObjectURL(photoPreview);
  }, [photoPreview]);

  const onPhotoPicked = (file) => {
    setPhotoFile(file);
    setPhotoPreview(URL.createObjectURL(file));
    setRemovePhoto(false);
  };
  const onPhotoRemoved = () => {
    setPhotoFile(null);
    setPhotoPreview(null);
    setRemovePhoto(true);
  };

  // "Cerrar sesión" from the header: drop the resolved aspirant and every trace
  // of their data, back to the login gate. The navigate() clears the router
  // state (nuip, justRegistered) and the ?t= magic-link token from the URL —
  // otherwise a reload would silently log them back in.
  const handleApplicantLogout = useCallback(() => {
    setNuip(null); setApplicant(null); setApplications([]); setForm(null);
    setSelectedPostId(GENERAL_POSTULACION); setFiles({}); setUploaded(false); setApplying(null);
    setChannel("EMAIL"); setDest(""); setCode(""); setOtpSent(false); setPhoneConfirmation(null);
    setBusy(false); setError(null); setInfo(null);
    setPhotoFile(null); setPhotoPreview(null); setRemovePhoto(false);
    setEvidence({}); setProfileFiles({});
    setSavingProfile(false); setProfileError(null); setProfileSaved(false);
    setPfEmailOtpSent(false); setPfEmailCode(""); setPfEmailVerified(false);
    setPfVerifiedEmail(""); setPfEmailInfo(null);
    setPfPhoneConfirmation(null); setPfPhoneCode(""); setPfPhoneVerified(false);
    setPfVerifiedPhone(""); setPfPhoneToken(null); setPfPhoneInfo(null);
    loginCooldown.reset(); pfPhoneCooldown.reset();
    clearRecaptcha();
    navigate("/submission/continuar", { replace: true });
  }, [loginCooldown.reset, pfPhoneCooldown.reset, navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  // Publish the aspirant's identity to the layout Header (name + photo + logout).
  // Keyed on photoUrl, so a pending crop shows up there right away too.
  const setIdentity = applicantSession && applicantSession.setIdentity;
  useEffect(() => {
    if (!setIdentity) return undefined;
    const fullName = applicant
      ? `${applicant.firstName || ""} ${applicant.lastName || ""}`.trim()
      : "";
    setIdentity(applicant ? { fullName, photoUrl, onLogout: handleApplicantLogout } : null);
    return () => setIdentity(null);
  }, [setIdentity, applicant, photoUrl, handleApplicantLogout]);

  // Editing a verified contact invalidates its verification.
  const onEmailEdit = (v) => {
    setField("email", v);
    if (pfEmailVerified && norm(v) !== norm(pfVerifiedEmail)) {
      setPfEmailVerified(false); setPfEmailOtpSent(false); setPfEmailCode(""); setPfEmailInfo(null);
    }
  };
  // Editing invalidates any pending code too: one sent to the previous number
  // can't confirm this one. The cooldown is dropped so a mistyped number can be
  // corrected right away — it guards against hammering ONE number, not this.
  const onPhoneEdit = (v) => {
    setField("mobilePhone", v);
    if (toE164(v) !== pfVerifiedPhone) {
      setPfPhoneVerified(false); setPfPhoneConfirmation(null); setPfPhoneCode(""); setPfPhoneToken(null); setPfPhoneInfo(null);
      pfPhoneCooldown.reset();
    }
  };

  const pfSendEmailOtp = async () => {
    const dest = (form.email || "").trim();
    if (!EMAIL_RE.test(dest)) { setProfileError("Ingresa un correo electrónico válido."); return; }
    setSavingProfile(true); setProfileError(null);
    // Fail fast (before spending an OTP) if the email belongs to another cédula.
    const chk = await checkApplicantContact({ nuip, email: dest });
    if (chk.ok && chk.json?.emailInUse) { setSavingProfile(false); setProfileError(applicantErrorMessage("EMAIL_IN_USE")); return; }
    const { ok, json } = await sendApplicantOtp({ recipient: dest });
    setSavingProfile(false);
    if (!ok) { setProfileError(json?.error || "No fue posible enviar el código."); return; }
    setPfEmailOtpSent(true);
    setPfEmailInfo(`Enviamos un código a ${dest}. Vence en ${json?.expiresInMinutes || 10} minutos.`);
  };
  const pfVerifyEmailOtp = async () => {
    setSavingProfile(true); setProfileError(null);
    const { ok, json } = await verifyApplicantOtp({ recipient: (form.email || "").trim(), code: pfEmailCode.trim() });
    setSavingProfile(false);
    if (!ok) { setProfileError(json?.error === "OTP_EXPIRED" ? "El código expiró. Solicita uno nuevo." : "Código incorrecto."); return; }
    setPfEmailVerified(true); setPfVerifiedEmail((form.email || "").trim()); setPfEmailInfo(null);
  };

  const pfSendPhone = async () => {
    if (!isValidPhone(form.mobilePhone)) { setProfileError("Ingresa un número de celular válido."); return; }
    setSavingProfile(true); setProfileError(null);
    try {
      const chk = await checkApplicantContact({ nuip, phone: toE164(form.mobilePhone) });
      if (chk.ok && chk.json?.phoneInUse) { setProfileError(applicantErrorMessage("PHONE_IN_USE")); setSavingProfile(false); return; }
      const confirmation = await sendPhoneCode(form.mobilePhone, RECAPTCHA_ID, { testMode: !!cfg.smsTestMode });
      setPfPhoneConfirmation(confirmation);
      setPfPhoneInfo(phoneSendInfo(toE164(form.mobilePhone), !!cfg.smsTestMode));
      pfPhoneCooldown.start();
    } catch (e) {
      setProfileError(phoneAuthErrorMessage(e));
    } finally { setSavingProfile(false); }
  };
  const pfVerifyPhone = async () => {
    if (!pfPhoneConfirmation) return;
    setSavingProfile(true); setProfileError(null);
    try {
      const { idToken } = await confirmPhoneCode(pfPhoneConfirmation, pfPhoneCode);
      setPfPhoneToken(idToken); setPfPhoneVerified(true); setPfVerifiedPhone(toE164(form.mobilePhone));
      setPfPhoneInfo(null); setPfPhoneConfirmation(null); pfPhoneCooldown.reset();
    } catch (e) {
      setProfileError(phoneAuthErrorMessage(e));
    } finally { setSavingProfile(false); }
  };

  // Los documentos de perfil se guardan junto con el perfil: pertenecen a la
  // persona y, en el caso de los soportes, al registro que certifican. Se filtran
  // contra los registros que realmente se van a guardar — un soporte atado a una
  // fila que el aspirante borró no certifica nada y el intake rechazaría todo el
  // envío por él.
  const collectProfileFiles = (payloadData) => {
    const list = [];
    unboundProfileDocs.forEach((d) => {
      (profileFiles[d.key] || []).forEach((picked) => {
        if (picked?.file) {
          list.push({ type: d.key, file: picked.file, scope: "profile", replaceFileId: picked.replaceFileId });
        }
      });
    });
    const live = [
      [eduDoc, new Set((payloadData.education || []).map((e) => e.id))],
      [expDoc, new Set((payloadData.workExperience || []).map((e) => e.id))],
    ];
    live.forEach(([doc, alive]) => {
      if (!doc) return;
      Object.entries(evidence[doc.key] || {}).forEach(([entryId, arr]) => {
        if (!alive.has(entryId)) return;
        (arr || []).forEach((picked) => {
          if (picked?.file) {
            list.push({
              type: doc.key, file: picked.file, scope: "profile", entryId,
              replaceFileId: picked.replaceFileId,
            });
          }
        });
      });
    });
    return list;
  };

  const handleSaveProfile = async () => {
    if (!form) return false;
    setSavingProfile(true); setProfileError(null); setProfileSaved(false);
    const data = buildApplicantData(form);
    const { ok, json } = await registerApplicant({
      data,
      callId: activePostId, stage, step: "perfil",
      files: collectProfileFiles(data),
      photo: photoFile, removePhoto,
      idempotencyKey: `applicant:${nuip}:perfil-edit:${Date.now()}`,
      phoneToken: phoneChanged ? pfPhoneToken : undefined,
    });
    setSavingProfile(false);
    if (!ok) { setProfileError(applicantErrorMessage(json?.error) || json?.error || "No fue posible guardar tus cambios."); return false; }
    const a = await getApplicant(nuip);
    setApplicant(a);
    await loadApplications(nuip);
    if (a) resetForm(a);
    setPfEmailVerified(false); setPfEmailOtpSent(false); setPfEmailCode(""); setPfEmailInfo(null);
    setPfPhoneVerified(false); setPfPhoneToken(null); setPfPhoneCode(""); setPfPhoneInfo(null);
    // Drop the pending photo so the stored photoPath drives the avatar again.
    setPhotoFile(null); setPhotoPreview(null); setRemovePhoto(false);
    // …y los documentos ya subidos, que ahora llegan desde el perfil guardado.
    setEvidence({}); setProfileFiles({});
    setProfileSaved(true);
    return true;
  };

  const handleFile = (type, slot, file, replaceFileId = null) => {
    if (file && file.size > maxFileBytes) { setError(`"${file.name}" supera ${maxFileMb} MB.`); return; }
    setError(null);
    setFiles((prev) => {
      const arr = Array.isArray(prev[type]) ? [...prev[type]] : [];
      arr[slot] = file ? { file, replaceFileId } : undefined;
      return { ...prev, [type]: arr };
    });
  };

  const handleUpload = async () => {
    const fileList = [];
    Object.entries(files).forEach(([type, arr]) => {
      (arr || []).forEach((picked) => {
        if (picked?.file) fileList.push({ type, file: picked.file, replaceFileId: picked.replaceFileId });
      });
    });
    if (!fileList.length) { setError("Selecciona al menos un documento."); return; }
    setBusy(true); setError(null);
    const { ok, json } = await registerApplicant({
      data: {
        nuip: applicant.nuip, firstName: applicant.firstName, lastName: applicant.lastName, email: applicant.email,
      },
      callId: activePostId, stage, step: `docs_stage${stage}`, files: fileList,
      idempotencyKey: `applicant:${applicant.nuip}:resume:${Date.now()}`,
    });
    setBusy(false);
    if (!ok) { setError(applicantErrorMessage(json?.error) || "No fue posible cargar los documentos."); return; }
    setUploaded(true);
    await loadApplications(nuip);
    setFiles({});
  };

  // Apply to another call: the backend reuses docs already uploaded in
  // General for the call's required stage-1 keys; any still missing are
  // prompted in the document section once the new postulación is selected.
  const handleApply = async (postId) => {
    if (!applicant) return;
    setApplying(postId); setError(null); setUploaded(false);
    const { ok, json } = await registerApplicant({
      data: {
        nuip: applicant.nuip, firstName: applicant.firstName, lastName: applicant.lastName, email: applicant.email,
      },
      callId: postId, stage: 1, step: "aplicar",
      idempotencyKey: `applicant:${applicant.nuip}:aplicar:${postId}:${Date.now()}`,
    });
    setApplying(null);
    if (!ok) { setError(applicantErrorMessage(json?.error) || "No fue posible aplicar a la convocatoria."); return; }
    await loadApplications(nuip);
    setSelectedPostId(postId);
    setFiles({});
  };

  // --- Login gate (no nuip yet) ---
  if (!nuip) {
    return (
      <Container maxWidth="sm" sx={{ py: 4 }}>
        <Typography variant="h4" align="center" gutterBottom>Continuar mi postulación</Typography>
        <Paper elevation={2} sx={{ p: { xs: 2, md: 4 }, mt: 3 }}>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {info && <Alert severity="info" sx={{ mb: 2 }}>{info}</Alert>}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Ingresa con el correo o el celular que usaste al postularte. Te enviaremos un código de acceso.
          </Typography>
          <ToggleButtonGroup
            color="primary"
            exclusive
            fullWidth
            size="small"
            value={channel}
            onChange={onChannelChange}
            sx={{ mb: 2 }}
          >
            <ToggleButton value="EMAIL">Correo</ToggleButton>
            <ToggleButton value="PHONE">Teléfono</ToggleButton>
          </ToggleButtonGroup>

          {channel === "EMAIL" ? (
            <Stack spacing={2}>
              <TextField label="Correo electrónico" type="email" value={dest} onChange={(e) => setDest(e.target.value)} disabled={otpSent} fullWidth />
              {otpSent && (
                <TextField label="Código de 6 dígitos" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} fullWidth />
              )}
              {!otpSent ? (
                <Button variant="contained" onClick={handleSendOtp} disabled={busy}>{busy ? "Enviando…" : "Enviar código"}</Button>
              ) : (
                <Stack direction="row" spacing={2}>
                  <Button variant="text" onClick={() => { setOtpSent(false); setCode(""); setInfo(null); }}>Cambiar contacto</Button>
                  <Button variant="contained" onClick={handleVerifyOtp} disabled={busy || code.length !== 6} sx={{ ml: "auto" }}>
                    {busy ? "Verificando…" : "Ingresar"}
                  </Button>
                </Stack>
              )}
            </Stack>
          ) : (
            <Stack spacing={2}>
              {SMS_TEST_MODE_VISIBLE && cfg.smsTestMode && (
                <Alert severity="warning">
                  Modo de prueba SMS activo: no se envían SMS reales. Solo funcionan los
                  números de prueba registrados en Firebase.
                </Alert>
              )}
              <MuiTelInput label="Celular" defaultCountry="CO" preferredCountries={["CO", "US", "ES", "MX", "PE", "EC", "VE"]} value={dest} onChange={setDest} disabled={!!phoneConfirmation} fullWidth />
              {phoneConfirmation && (
                <>
                  <TextField label="Código de 6 dígitos" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} fullWidth />
                  <Typography variant="caption" color="text.secondary">{NO_SMS_HINT}</Typography>
                </>
              )}
              {!phoneConfirmation ? (
                <Button variant="contained" onClick={handleSendPhone} disabled={busy || !isValidPhone(dest) || loginCooldown.seconds > 0}>
                  {busy ? "Enviando…" : loginCooldown.seconds > 0 ? `Reintentar en ${loginCooldown.seconds}s` : "Enviar código por SMS"}
                </Button>
              ) : (
                <Stack direction="row" spacing={2}>
                  <Button variant="text" onClick={() => { setPhoneConfirmation(null); setCode(""); setInfo(null); clearRecaptcha(); }}>Cambiar contacto</Button>
                  <Button variant="contained" onClick={handleVerifyPhone} disabled={busy || code.length !== 6} sx={{ ml: "auto" }}>
                    {busy ? "Verificando…" : "Ingresar"}
                  </Button>
                </Stack>
              )}
            </Stack>
          )}
        </Paper>
        {/* Invisible reCAPTCHA host for Firebase Phone Auth. */}
        <div id={RECAPTCHA_ID} />
      </Container>
    );
  }

  // --- Side menu -----------------------------------------------------------
  // Each profile area gets its own section; the per-call documents live under
  // "Convocatorias". Pending counts are what still owes a document there.
  const picked = (obj) => Object.values(obj || {}).some((v) => (Array.isArray(v)
    ? v.some(Boolean)
    : Object.values(v || {}).some((arr) => Array.isArray(arr) && arr.some(Boolean))));
  const profileDirty = !!form && (JSON.stringify(form) !== formBaseline
    || !!photoFile || removePhoto || picked(evidence) || picked(profileFiles));
  const owing = (doc, entries, sectionKey, isFilled) => (!doc || !form ? 0 : (entries || [])
    .filter(isFilled)
    .filter((e) => entryNeedsEvidence(e, sectionKey)
      && storedFilesFor(doc.key)(e.id).length === 0
      && !pendingEvidenceFor(doc.key)(e.id).some(Boolean)).length);
  const missingPersonalDocs = unboundProfileDocs.filter((d) => d.required !== false
    && docFiles(storedProfileDocs[d.key]).length === 0
    && !(profileFiles[d.key] || []).some(Boolean)).length;
  const sections = [
    { id: "personal", label: "Datos personales", profile: true, pending: 0 },
    unboundProfileDocs.length > 0 && { id: "documents", label: "Mis documentos", profile: true, pending: missingPersonalDocs },
    eduDoc && { id: "education", label: "Formación", profile: true,
      pending: owing(eduDoc, form?.education, "education", hasEducationEntry) },
    expDoc && { id: "experience", label: "Experiencia", profile: true,
      pending: owing(expDoc, form?.workExperience, "workExperience", hasExperienceEntry) },
    { id: "calls", label: "Convocatorias", profile: false, pending: faltantesObligatorios.length },
  ].filter(Boolean);
  const current = sections.find((x) => x.id === section) || sections[0];

  // Leaving a profile section with unsaved edits saves them first; a save that
  // can't go through (unverified new email/phone, a server error) keeps the
  // applicant where the problem is.
  const goToSection = async (next) => {
    if (next === current.id || savingProfile) return;
    if (current.profile && profileDirty) {
      if (!canSaveProfile) {
        setProfileError("Verifica tu nuevo correo o celular: al cambiar de sección guardamos tus cambios.");
        return;
      }
      if (!(await handleSaveProfile())) return;
    }
    setSection(next);
    const params = new URLSearchParams(window.location.search);
    params.set("seccion", next);
    window.history.replaceState(window.history.state, "", `${window.location.pathname}?${params}`);
  };

  // --- Resume view (nuip resolved) ---
  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" align="center" gutterBottom>Mi perfil</Typography>
      <Paper elevation={2} sx={{ p: { xs: 2, md: 4 }, mt: 3 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {justRegistered && <Alert severity="success" sx={{ mb: 2 }}>¡Registro completado! Revisa tus datos y, si lo deseas, aplica a otras convocatorias.</Alert>}
        {uploaded && <Alert severity="success" sx={{ mb: 2 }}>Documentos cargados correctamente.</Alert>}

        {busy && !applicant ? (
          <Typography>Cargando…</Typography>
        ) : !applicant ? (
          <Alert severity="warning">No encontramos tu información.</Alert>
        ) : (
          <>
            <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
              <Typography variant="h6">{applicant.firstName} {applicant.lastName}</Typography>
              <Chip size="small" label={labelForStatus(APPLICANT_FACING_STATUS_LABELS, application?.status || "APPLIED")} />
              <Chip size="small" variant="outlined" label={`${application?.completionPercentage || 0}% completo`} />
            </Stack>

            {/* La selección es una decisión interna: al aspirante sólo se le pide
                la documentación adicional, sin anticiparle el resultado. */}
            {stage === 2 && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Tu postulación avanzó a la etapa de verificación. Para continuar, carga los
                documentos adicionales (antecedentes y documentos administrativos) que se te
                solicitan más abajo.
              </Alert>
            )}

            <Grid container spacing={3}>
              {/* Side menu (a scrolling tab bar on narrow screens). */}
              <Grid item xs={12} md={3}>
                <Tabs
                  orientation={wideMenu ? "vertical" : "horizontal"}
                  variant="scrollable"
                  scrollButtons="auto"
                  value={current.id}
                  onChange={(_e, v) => goToSection(v)}
                  sx={wideMenu ? { borderRight: 1, borderColor: "divider" } : { borderBottom: 1, borderColor: "divider" }}
                >
                  {sections.map((x) => (
                    <Tab
                      key={x.id}
                      value={x.id}
                      disabled={savingProfile}
                      sx={{ alignItems: wideMenu ? "flex-start" : "center", textAlign: "left", minHeight: 48 }}
                      label={(
                        <Stack direction="row" spacing={1} alignItems="center">
                          <span>{x.label}</span>
                          {x.pending > 0 && <Chip size="small" color="warning" label={x.pending} />}
                          {x.id === current.id && current.profile && profileDirty && (
                            <Chip size="small" variant="outlined" label="Sin guardar" />
                          )}
                        </Stack>
                      )}
                    />
                  ))}
                </Tabs>
              </Grid>

              <Grid item xs={12} md={9}>
                <Typography variant="h6" gutterBottom>{current.label}</Typography>

                {/* --- Editable profile: one section at a time, one save for all of it --- */}
                {current.profile && form && (
                  <>
                    {profileError && <Alert severity="error" sx={{ mb: 2 }}>{profileError}</Alert>}
                    {profileSaved && <Alert severity="success" sx={{ mb: 2 }}>Datos actualizados correctamente.</Alert>}
                    <Grid container spacing={2}>
                      {current.id === "personal" && (
                        <>
                          {/* Foto de perfil — se guarda junto con el resto de los datos. */}
                          <Grid item xs={12}>
                            <ApplicantPhotoUpload
                              currentUrl={photoUrl}
                              alt={`${form.firstName} ${form.lastName}`.trim()}
                              disabled={savingProfile}
                              onChange={onPhotoPicked}
                              onRemove={onPhotoRemoved}
                            />
                            {(photoFile || removePhoto) && (
                              <Typography variant="caption" color="text.secondary" display="block" align="center">
                                Guarda los cambios para aplicar la foto.
                              </Typography>
                            )}
                          </Grid>

                          {/* Permanent identity fields — read only. */}
                          <Grid item xs={12} sm={6}>
                            <TextField fullWidth disabled label="Nombre completo" value={`${form.firstName} ${form.lastName}`.trim()} />
                          </Grid>
                          <Grid item xs={12} sm={3}>
                            <TextField fullWidth disabled label="Tipo de documento" value={form.documentType || "—"} />
                          </Grid>
                          <Grid item xs={12} sm={3}>
                            <TextField fullWidth disabled label="Número" value={form.nuip || ""} />
                          </Grid>

                          {/* Correo — re-verify on change. */}
                          <Grid item xs={12} sm={6}>
                            <TextField fullWidth type="email" label="Correo electrónico" value={form.email}
                              onChange={(e) => onEmailEdit(e.target.value)} />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            {!emailChanged ? null : emailOk ? (
                              <Alert severity="success" sx={{ py: 0.5 }}>Correo verificado ✓</Alert>
                            ) : (
                              <Stack spacing={1}>
                                {pfEmailInfo && <Alert severity="info" sx={{ py: 0.5 }}>{pfEmailInfo}</Alert>}
                                {!pfEmailOtpSent ? (
                                  <Button variant="outlined" size="small" onClick={pfSendEmailOtp}
                                    disabled={savingProfile || !EMAIL_RE.test((form.email || "").trim())}>
                                    {savingProfile ? "Enviando…" : "Verificar nuevo correo"}
                                  </Button>
                                ) : (
                                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }}>
                                    <TextField size="small" label="Código" value={pfEmailCode}
                                      onChange={(e) => setPfEmailCode(e.target.value.replace(/\D/g, "").slice(0, 6))} />
                                    <Button variant="contained" size="small" onClick={pfVerifyEmailOtp} disabled={savingProfile || pfEmailCode.length !== 6}>
                                      {savingProfile ? "Verificando…" : "Verificar"}
                                    </Button>
                                    <Button variant="text" size="small" onClick={pfSendEmailOtp} disabled={savingProfile}>Reenviar</Button>
                                  </Stack>
                                )}
                              </Stack>
                            )}
                          </Grid>

                          {/* Celular — re-verify on change. */}
                          <Grid item xs={12} sm={6}>
                            <MuiTelInput fullWidth label="Celular" defaultCountry="CO"
                              preferredCountries={["CO", "US", "ES", "MX", "PE", "EC", "VE"]}
                              value={form.mobilePhone} onChange={onPhoneEdit} />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            {!phoneChanged ? null : phoneOk ? (
                              <Alert severity="success" sx={{ py: 0.5 }}>Celular verificado ✓</Alert>
                            ) : (
                              <Stack spacing={1}>
                                {SMS_TEST_MODE_VISIBLE && cfg.smsTestMode && (
                                  <Alert severity="warning" sx={{ py: 0.5 }}>
                                    Modo de prueba SMS activo: no se envían SMS reales.
                                  </Alert>
                                )}
                                {pfPhoneInfo && <Alert severity="info" sx={{ py: 0.5 }}>{pfPhoneInfo}</Alert>}
                                {!pfPhoneConfirmation ? (
                                  <Button variant="outlined" size="small" onClick={pfSendPhone}
                                    disabled={savingProfile || !isValidPhone(form.mobilePhone)}>
                                    {savingProfile ? "Enviando…" : "Verificar nuevo celular"}
                                  </Button>
                                ) : (
                                  <>
                                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }}>
                                      <TextField size="small" label="Código SMS" value={pfPhoneCode}
                                        onChange={(e) => setPfPhoneCode(e.target.value.replace(/\D/g, "").slice(0, 6))} />
                                      <Button variant="contained" size="small" onClick={pfVerifyPhone} disabled={savingProfile || pfPhoneCode.length !== 6}>
                                        {savingProfile ? "Verificando…" : "Verificar"}
                                      </Button>
                                      <Button variant="text" size="small" onClick={pfSendPhone}
                                        disabled={savingProfile || pfPhoneCooldown.seconds > 0}>
                                        {pfPhoneCooldown.seconds > 0 ? `Reenviar en ${pfPhoneCooldown.seconds}s` : "Reenviar"}
                                      </Button>
                                    </Stack>
                                    <Typography variant="caption" color="text.secondary">{NO_SMS_HINT}</Typography>
                                  </>
                                )}
                              </Stack>
                            )}
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <TextField fullWidth type="date" label="Fecha de nacimiento" InputLabelProps={{ shrink: true }}
                              value={form.birthDate} onChange={(e) => setField("birthDate", e.target.value)} />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField fullWidth label="Dirección" value={form.address} onChange={(e) => setField("address", e.target.value)} />
                          </Grid>

                        </>
                      )}
                      {current.id === "documents" && (
                        <>
                          {/* Documentos de la persona (cédula y demás): se guardan con el
                              perfil y valen para todas las convocatorias. */}
                          {unboundProfileDocs.length > 0 && (
                            <Grid item xs={12}>
                              <Grid container spacing={2}>
                                {unboundProfileDocs.map((docDef) => {
                                  const stored = docFiles(storedProfileDocs[docDef.key]);
                                  const estado = docStatusRollup(storedProfileDocs[docDef.key]);
                                  const picked = profileFiles[docDef.key] || [];
                                  const motivos = [...new Set(stored
                                    .filter((a) => a.status === "REJECTED" && a.rejectionComments)
                                    .map((a) => String(a.rejectionComments).trim()))];
                                  return (
                                    <Grid item xs={12} sm={6} key={docDef.key}>
                                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                        {docDef.label}{docDef.required === false ? " (opcional)" : ""}
                                      </Typography>
                                      {docDef.description && (
                                        <Typography variant="caption" color="text.secondary" display="block">{docDef.description}</Typography>
                                      )}
                                      {stored.length > 0 ? (
                                        <Chip sx={{ my: 0.5 }} size="small"
                                          color={estado === "VALIDATED" ? "success" : estado === "REJECTED" ? "error" : "info"}
                                          label={labelForStatus(DOC_FILE_STATUS_LABELS, estado)} />
                                      ) : docDef.required !== false ? (
                                        <Chip sx={{ my: 0.5 }} size="small" color="warning" variant="outlined" label="OBLIGATORIO" />
                                      ) : null}
                                      {estado === "REJECTED" && (
                                        <Alert severity="error" sx={{ my: 0.5, py: 0 }}>
                                          {motivos.length
                                            ? motivos.map((m, i) => <Typography key={i} variant="body2">{m}</Typography>)
                                            : <Typography variant="body2">Este documento fue rechazado.</Typography>}
                                        </Alert>
                                      )}
                                      <DocumentSlots
                                        docDef={docDef}
                                        storedFiles={stored}
                                        pending={picked}
                                        disabled={savingProfile}
                                        onView={openStoredFile}
                                        onPick={(slot, file, replaceFileId) =>
                                          handleProfileFile(docDef.key, slot, file, replaceFileId)}
                                      />
                                    </Grid>
                                  );
                                })}
                              </Grid>
                            </Grid>
                          )}

                        </>
                      )}
                      {current.id === "education" && (
                        <>
                          {/* Formación — repetible; cada nivel lleva su soporte adentro.
                              La sección sólo existe si su documento está habilitado. */}
                          {eduDoc && (
                            <Grid item xs={12}>
                              <EducationList
                                items={form.education}
                                levels={niveles}
                                institutions={instituciones}
                                openInstitutions={institucionesAbiertas}
                                openLevels={nivelesAbiertos}
                                disabled={savingProfile}
                                onChange={(next) => setField("education", next)}
                                evidenceDoc={eduDoc}
                                filesForEntry={storedFilesFor(eduDoc.key)}
                                pendingFor={pendingEvidenceFor(eduDoc.key)}
                                onPickEvidence={handleEvidence(eduDoc.key)}
                                onViewFile={openStoredFile}
                                onExtractCertificate={cfg?.educationAI?.enabled !== false ? extractEducationCertificate : undefined}
                                person={identityPerson}
                              />
                            </Grid>
                          )}

                        </>
                      )}
                      {current.id === "experience" && (
                        <>
                          {/* Experiencia — repetible; cada una lleva su certificado adentro. */}
                          {expDoc && (
                            <Grid item xs={12}>
                              <ExperienceList
                                items={form.workExperience}
                                disabled={savingProfile}
                                onChange={(next) => setField("workExperience", next)}
                                evidenceDoc={expDoc}
                                filesForEntry={storedFilesFor(expDoc.key)}
                                pendingFor={pendingEvidenceFor(expDoc.key)}
                                onPickEvidence={handleEvidence(expDoc.key)}
                                onViewFile={openStoredFile}
                                onExtractCertificate={cfg?.experienceAI?.enabled !== false ? extractExperienceCertificate : undefined}
                                person={identityPerson}
                              />
                            </Grid>
                          )}
                        </>
                      )}
                    </Grid>
                    <Box sx={{ mt: 2, textAlign: "right" }}>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                        Los documentos que adjuntes aquí se guardan junto con tus datos. Si cambias de sección, guardamos tus cambios.
                      </Typography>
                      <Button variant="contained" onClick={handleSaveProfile} disabled={!canSaveProfile}>
                        {savingProfile ? "Guardando…" : "Guardar cambios"}
                      </Button>
                    </Box>
                  </>
                )}

                {/* --- Convocatorias: switcher, other calls and the per-call documents --- */}
                {current.id === "calls" && (
                  <>
                    {/* Convocatoria switcher — one toggle per postulación the aspirant is in. */}
                    {applications.length > 0 && (
                      <ToggleButtonGroup
                        color="primary" exclusive size="small" value={activePostId}
                        onChange={(_e, v) => { if (v) { setSelectedPostId(v); setFiles({}); setUploaded(false); } }}
                        sx={{ mb: 2, flexWrap: "wrap" }}
                      >
                        {applications.map((p) => (
                          <ToggleButton key={p.callId} value={p.callId}>
                            {p.callName || callName(cfg, p.callId)}
                          </ToggleButton>
                        ))}
                      </ToggleButtonGroup>
                    )}

                    {/* Other active calls the aspirant can still apply to. */}
                    {otherCalls.length > 0 && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>Otras convocatorias</Typography>
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                          {otherCalls.map((p) => (
                            <Button key={p.id} variant="outlined" size="small"
                              disabled={applying === p.id}
                              onClick={() => handleApply(p.id)}>
                              {applying === p.id ? "Aplicando…" : `Aplicar a ${p.name}`}
                            </Button>
                          ))}
                        </Stack>
                      </Box>
                    )}
                    <Typography variant="subtitle1" gutterBottom>
                      {stage === 2
                        ? "Documentos (documentación adicional solicitada)"
                        : `Documentos de «${application?.callName || callName(cfg, activePostId)}»`}
                    </Typography>
                    {stageDocs.length === 0 && (
                      <Alert severity="success" sx={{ mb: 2 }}>
                        Esta convocatoria no pide documentos adicionales: con tu hoja de vida y sus soportes
                        es suficiente.
                      </Alert>
                    )}
                    {faltantesObligatorios.length > 0 && (
                      <Alert severity="warning" sx={{ mb: 2 }}>
                        Te falta{faltantesObligatorios.length > 1 ? "n" : ""} {faltantesObligatorios.length} documento
                        {faltantesObligatorios.length > 1 ? "s" : ""} obligatorio{faltantesObligatorios.length > 1 ? "s" : ""} por cargar.
                      </Alert>
                    )}
                    <Grid container spacing={2}>
                      {stageDocs.map((docDef) => {
                        const { key: type, label, description } = docDef;
                        const current = files[type] || [];
                        const existingEstado = docStatusRollup(existingDocs[type]);
                        // Already-uploaded attachments for this type. Must NOT be named
                        // `files`: that shadows the `files` state holding the newly picked
                        // uploads, and `current` above reads it (TDZ crash on render).
                        const existingFiles = (existingDocs[type] && existingDocs[type].files) || [];
                        const isRequired = docDef.required !== false;
                        return (
                          <Grid item xs={12} sm={6} key={type}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {label}{isRequired ? "" : " (opcional)"}
                            </Typography>
                            {description && (
                              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>{description}</Typography>
                            )}
                            {existingEstado !== "PENDING" ? (
                              <Chip sx={{ my: 0.5 }} size="small"
                                color={existingEstado === "VALIDATED" ? "success" : existingEstado === "REJECTED" ? "error" : "info"}
                                label={labelForStatus(DOC_FILE_STATUS_LABELS, existingEstado)} />
                            ) : isRequired ? (
                              <Chip sx={{ my: 0.5 }} size="small" color="warning" variant="outlined" label="OBLIGATORIO" />
                            ) : null}
                            {/* Why it was rejected: without this the aspirant only sees a red
                                chip and has no idea what to correct. One line per rejected
                                file for multi-file documents. */}
                            {existingEstado === "REJECTED" && (() => {
                              const motivos = [...new Set(
                                existingFiles
                                  .filter((a) => a && a.status === "REJECTED" && a.rejectionComments)
                                  .map((a) => String(a.rejectionComments).trim())
                              )];
                              return (
                                <Alert severity="error" sx={{ my: 0.5, py: 0 }}>
                                  {motivos.length > 0 ? (
                                    motivos.map((m, i) => (
                                      <Typography key={i} variant="body2" sx={{ display: "block" }}>{m}</Typography>
                                    ))
                                  ) : (
                                    <Typography variant="body2">Este documento fue rechazado.</Typography>
                                  )}
                                  <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                                    Vuelve a cargar este documento.
                                  </Typography>
                                </Alert>
                              );
                            })()}
                            <DocumentSlots
                              docDef={docDef}
                              storedFiles={existingFiles}
                              pending={current}
                              disabled={busy}
                              onView={openStoredFile}
                              onPick={(slot, file, replaceFileId) => handleFile(type, slot, file, replaceFileId)}
                            />
                          </Grid>
                        );
                      })}
                    </Grid>
                    {stageDocs.length > 0 && (
                      <Box sx={{ mt: 3, textAlign: "right" }}>
                        <Button variant="contained" onClick={handleUpload} disabled={busy}>{busy ? "Cargando…" : "Cargar documentos"}</Button>
                      </Box>
                    )}
                  </>
                )}
              </Grid>
            </Grid>
          </>
        )}
      </Paper>
    </Container>
  );
}
